import { type ReactElement, useState } from 'react';
import { featureApi } from '../../lib/feature-api';
import { useCurrentProject } from '../../hooks/use-current-project';

interface DeployForm {
  name: string;
  symbol: string;
  description: string;
  imageUrl: string;
  twitter: string;
  telegram: string;
  website: string;
  initialBuySol: number;
  slippage: number;
  priorityFee: number;
}

interface DeploymentResult {
  id: string;
  name: string;
  symbol: string;
  mintAddress: string;
  txSignature: string;
  pumpFunUrl: string;
  explorerUrl: string;
  deployerWallet: string;
}

const inputClass =
  'w-full rounded-lg border border-white/[0.1] bg-white/[0.04] px-4 py-3 text-white placeholder:text-white/20 focus:border-[#c4f70e]/50 focus:outline-none text-sm';
const labelClass = 'text-xs text-white/40 mb-1 block';

export function TokenDeployPage(): ReactElement {
  const { projectId } = useCurrentProject();
  const [form, setForm] = useState<DeployForm>({
    name: '',
    symbol: '',
    description: '',
    imageUrl: '',
    twitter: '',
    telegram: '',
    website: '',
    initialBuySol: 0,
    slippage: 10,
    priorityFee: 0.0005,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DeploymentResult | null>(null);

  const update = (field: keyof DeployForm, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!projectId) {
      setError('Please select a project first');
      return;
    }
    if (!form.name.trim() || !form.symbol.trim()) {
      setError('Token name and symbol are required');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await featureApi.post<{ data: DeploymentResult }>('/tokens/deploy', {
        projectId,
        name: form.name,
        symbol: form.symbol,
        description: form.description || undefined,
        imageUrl: form.imageUrl || undefined,
        twitter: form.twitter || undefined,
        telegram: form.telegram || undefined,
        website: form.website || undefined,
        initialBuySol: form.initialBuySol,
        slippage: form.slippage,
        priorityFee: form.priorityFee,
      });
      setResult(res.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Deployment failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Deploy Token</h1>
        <p className="mt-1 text-sm text-white/40">Launch a new token on Pump.fun</p>
      </div>

      {result && (
        <div className="rounded-lg border border-[#c4f70e]/20 bg-[#c4f70e]/5 p-4">
          <p className="text-sm font-semibold text-[#c4f70e]">Token deployed!</p>
          <div className="mt-2 space-y-1 text-sm text-white/70">
            <p>
              Name: <span className="text-white">{result.name}</span>
            </p>
            <p>
              Symbol: <span className="text-white">{result.symbol}</span>
            </p>
            <p className="break-all">
              Mint:{' '}
              <a
                href={result.pumpFunUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[#c4f70e] hover:underline"
              >
                {result.mintAddress}
              </a>
            </p>
            <p className="break-all">
              Tx:{' '}
              <a
                href={result.explorerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[#c4f70e] hover:underline"
              >
                {result.txSignature}
              </a>
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className={labelClass}>Token Name *</label>
          <input
            className={inputClass}
            placeholder="e.g. Drama Coin"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass}>Symbol *</label>
          <input
            className={inputClass}
            placeholder="e.g. DRAMA"
            value={form.symbol}
            onChange={(e) => update('symbol', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass}>Description</label>
          <textarea
            className={inputClass}
            rows={3}
            placeholder="What is this token about?"
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass}>Image URL</label>
          <input
            className={inputClass}
            placeholder="https://..."
            value={form.imageUrl}
            onChange={(e) => update('imageUrl', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className={labelClass}>Twitter / X</label>
            <input
              className={inputClass}
              placeholder="@handle"
              value={form.twitter}
              onChange={(e) => update('twitter', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Telegram</label>
            <input
              className={inputClass}
              placeholder="t.me/..."
              value={form.telegram}
              onChange={(e) => update('telegram', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Website</label>
            <input
              className={inputClass}
              placeholder="https://..."
              value={form.website}
              onChange={(e) => update('website', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className={labelClass}>Initial Buy (SOL)</label>
            <input
              className={inputClass}
              type="number"
              min={0}
              step={0.01}
              placeholder="0"
              value={form.initialBuySol}
              onChange={(e) => update('initialBuySol', parseFloat(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className={labelClass}>Slippage (%)</label>
            <input
              className={inputClass}
              type="number"
              min={0}
              step={1}
              placeholder="10"
              value={form.slippage}
              onChange={(e) => update('slippage', parseFloat(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className={labelClass}>Priority Fee (SOL)</label>
            <input
              className={inputClass}
              type="number"
              min={0}
              step={0.0001}
              placeholder="0.0005"
              value={form.priorityFee}
              onChange={(e) => update('priorityFee', parseFloat(e.target.value) || 0)}
            />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !projectId}
          className="w-full rounded-lg bg-[#c4f70e] px-4 py-3 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {isSubmitting ? 'Deploying...' : 'Deploy Token'}
        </button>
      </div>
    </div>
  );
}
